import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { createAndFireSafe, createRestFailedEvent, createRestSucceededEvent, extractErrorInfo } from '../analytics';
import { ReactionStatus } from '../types';
import { batch, batchByKey } from './batched';
import { addOne, byEmojiId, flattenAris, getReactionsSortFunction, isRealErrorFromService, readyState, removeOne, updateByEmojiId } from './utils';
import { SAMPLING_RATE_REACTIONS_RENDERED_EXP } from '../shared/constants';
import { ReactionsAdd, ReactionDetailsFetch, ReactionsRemove, sampledReactionsRendered } from '../ufo';
import { ReactionUpdateType } from '../types/reaction';

/**
 * Set of all available UFO experiences relating to reaction element
 */
export var ufoExperiences = {
  /**
   * Experience when a reaction emoji gets added
   */
  add: ReactionsAdd,
  /**
   * Experience when a reaction emoji gets removed/decrement
   */
  remove: ReactionsRemove,
  /**
   * Experience when the list of reactions gets rendered with sampling
   */
  render: function render(instanceId) {
    return sampledReactionsRendered(instanceId);
  },
  /**
   * Experience when a reaction details gets fetched
   */
  fetchDetails: ReactionDetailsFetch
};

/**
 * Optional metadata information in the store used in sending the API client requests
 */

export var MemoryReactionsStore = /*#__PURE__*/function () {
  function MemoryReactionsStore(client, state, metadata) {
    var _this = this;
    _classCallCheck(this, MemoryReactionsStore);
    _defineProperty(this, "callbacks", []);
    /**
     * default initial store data
     */
    _defineProperty(this, "initialState", {
      reactions: {},
      flash: {},
      particleEffectByEmoji: {}
    });
    /**
     * Update the store state data with a new data
     * @param newState new store data
     */
    _defineProperty(this, "setState", function (newState) {
      _this.state = _objectSpread(_objectSpread({}, _this.state), newState);
      _this.triggerOnChange();
    });
    /**
     * Calls any onChange callbacks in the store using latest state data.
     */
    _defineProperty(this, "triggerOnChange", batch(function () {
      return _this.callbacks.forEach(function (callback) {
        return callback(_this.state);
      });
    }));
    _defineProperty(this, "setReactions", function (containerAri, ari, reactions) {
      _this.setState({
        reactions: _objectSpread(_objectSpread({}, _this.state.reactions), {}, _defineProperty({}, "".concat(containerAri, "|").concat(ari), reactions))
      });
    });
    _defineProperty(this, "handleDetailedReactionResponse", function (detailedReaction) {
      var containerAri = detailedReaction.containerAri,
        ari = detailedReaction.ari,
        emojiId = detailedReaction.emojiId;
      _this.withReaction(function (reaction) {
        return _objectSpread(_objectSpread({}, reaction), {}, {
          users: detailedReaction.users
        });
      })(containerAri, ari, emojiId);
    });
    _defineProperty(this, "flash", function (reaction) {
      _this.setFlash(reaction.containerAri, reaction.ari, reaction.emojiId, true);
      window.setTimeout(function () {
        return _this.setFlash(reaction.containerAri, reaction.ari, reaction.emojiId, false);
      }, 700);
    });
    _defineProperty(this, "displayParticleEffect", function (reaction) {
      _this.setParticleEffectForEmoji(reaction.containerAri, reaction.ari, reaction.emojiId, true);
      window.setTimeout(function () {
        _this.setParticleEffectForEmoji(reaction.containerAri, reaction.ari, reaction.emojiId, false);
      }, 1500);
    });
    _defineProperty(this, "optmisticUpdate", function (containerAri, ari, emojiId) {
      return function (updater) {
        _this.withReadyReaction(containerAri, ari)(function (reactionState) {
          var found = false;
          var reactions = reactionState.reactions.map(function (reaction) {
            if (reaction.emojiId === emojiId) {
              found = true;
              var updated = updater(reaction);
              if (updated) {
                return _objectSpread(_objectSpread({}, updated), {}, {
                  optimisticallyUpdated: true
                });
              }
            }
            return reaction;
          });
          if (!found) {
            var updated = updater({
              containerAri: containerAri,
              ari: ari,
              emojiId: emojiId,
              count: 0,
              reacted: false
            });
            if (updated) {
              reactions.push(_objectSpread(_objectSpread({}, updated), {}, {
                optimisticallyUpdated: true
              }));
            }
          }
          return readyState(reactions);
        });
      };
    });
    _defineProperty(this, "doAddReaction", function (reaction, onSuccess) {
      var containerAri = reaction.containerAri,
        ari = reaction.ari,
        emojiId = reaction.emojiId;
      _this.optmisticUpdate(containerAri, ari, emojiId)(addOne);
      _this.flash(reaction);
      _this.displayParticleEffect(reaction);
      var exp = ufoExperiences.add.getInstance("".concat(ari, "|").concat(emojiId));
      // ufo start reaction experience
      exp.start();
      exp.addMetadata({
        source: 'MemoryReactionsStore',
        storeMetadata: _this.metadata,
        ari: ari,
        containerAri: containerAri,
        emojiId: emojiId
      });
      _this.client.addReaction(containerAri, ari, emojiId, _this.metadata).then(function (_) {
        if (_this.createAnalyticsEvent) {
          createAndFireSafe(_this.createAnalyticsEvent, createRestSucceededEvent, 'addReaction');
        }
        // ufo add reaction success
        exp.success();
        onSuccess === null || onSuccess === void 0 || onSuccess(ReactionUpdateType.added, ari, emojiId, reaction.count + 1);
      }).catch(function (error) {
        if (isRealErrorFromService(error.code)) {
          _this.createAnalyticsEvent && createAndFireSafe(_this.createAnalyticsEvent, createRestFailedEvent, 'addReaction', error.code);
          // ufo add reaction failure
          exp.failure({
            metadata: {
              error: extractErrorInfo(error),
              reason: 'addReaction fetch failed'
            }
          });
        }
        return Promise.reject(error);
      });
    });
    _defineProperty(this, "doRemoveReaction", function (reaction, onSuccess) {
      var containerAri = reaction.containerAri,
        ari = reaction.ari,
        emojiId = reaction.emojiId;
      var exp = ufoExperiences.remove.getInstance("".concat(ari, "|").concat(emojiId));
      _this.setParticleEffectForEmoji(containerAri, ari, emojiId, false);

      // ufo start reaction experience

      exp.start();
      exp.addMetadata({
        source: 'MemoryReactionsStore',
        storeMetadata: _this.metadata,
        ari: ari,
        containerAri: containerAri,
        emojiId: emojiId
      });
      _this.optmisticUpdate(containerAri, ari, emojiId)(removeOne);
      _this.client.deleteReaction(containerAri, ari, emojiId, _this.metadata).then(function (_) {
        // ufo add reaction success
        exp.success();
        onSuccess === null || onSuccess === void 0 || onSuccess(ReactionUpdateType.removed, ari, emojiId, reaction.count - 1);
      }).catch(function (error) {
        // ufo add reaction failure
        exp.failure({
          metadata: {
            error: extractErrorInfo(error),
            reason: 'deleteReaction fetch failed'
          }
        });
      });
    });
    _defineProperty(this, "setCreateAnalyticsEvent", function (createAnalyticsEvent) {
      _this.createAnalyticsEvent = createAnalyticsEvent;
    });
    _defineProperty(this, "getReactions", batchByKey(function (containerAri, aris) {
      /**
       * TODO:
       * All reactions are usually fetched in a single call to reactions-service. Need to check why "getReactions" gets called randomly 1-2 times everytime on each fetch request despite using same containerAri.
       */
      var sampledExp = ufoExperiences.render(containerAri);
      var arisArr = aris.reduce(flattenAris);
      // ufo start reaction experience
      sampledExp.start({
        samplingRate: SAMPLING_RATE_REACTIONS_RENDERED_EXP
      });
      sampledExp.addMetadata({
        source: 'MemoryReactionsStore',
        storeMetadata: _this.metadata,
        containerAri: containerAri,
        aris: arisArr.join(',')
      });
      _this.client.getReactions(containerAri, arisArr).then(function (value) {
        Object.keys(value).map(function (ari) {
          var reactionsState = _this.getReactionsState(containerAri, ari);
          var reactions = reactionsState && reactionsState.status === ReactionStatus.ready ? reactionsState.reactions : undefined;
          _this.setReactions(containerAri, ari, readyState(value[ari].sort(getReactionsSortFunction(reactions))));
        });
      }).then(function () {
        if (_this.createAnalyticsEvent) {
          createAndFireSafe(_this.createAnalyticsEvent, createRestSucceededEvent, 'getReactions');
        }
        sampledExp.success();
      }).catch(function (error) {
        if (isRealErrorFromService(error.code)) {
          if (_this.createAnalyticsEvent) {
            createAndFireSafe(_this.createAnalyticsEvent, createRestFailedEvent, 'getReactions');
          }
          sampledExp.failure({
            metadata: {
              error: extractErrorInfo(error),
              reason: 'getReactions fetch failed'
            }
          });
        }
        var reactionsState = arisArr.reduce(function (acc, ari) {
          return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, "".concat(containerAri, "|").concat(ari), {
            reactions: [],
            status: ReactionStatus.error
          }));
        }, {});
        _this.setState(_objectSpread(_objectSpread({}, _this.state), {}, {
          reactions: _objectSpread(_objectSpread({}, _this.state.reactions), reactionsState)
        }));
      });
    }));
    _defineProperty(this, "toggleReaction", this.withReaction(this.doRemoveReaction, this.doAddReaction));
    _defineProperty(this, "addReaction", this.withReaction(this.flash, this.doAddReaction));
    _defineProperty(this, "getDetailedReaction", function (containerAri, ari, emojiId) {
      var exp = ufoExperiences.fetchDetails.getInstance("".concat(ari, "|").concat(emojiId));
      exp.start();
      exp.addMetadata({
        source: 'MemoryReactionsStore',
        storeMetadata: _this.metadata,
        ari: ari,
        containerAri: containerAri,
        emojiId: emojiId
      });
      _this.client.getDetailedReaction(containerAri, ari, emojiId).then(function (summary) {
        // ufo get reaction details success
        exp.success();
        _this.handleDetailedReactionResponse(summary);
      }).catch(function (error) {
        // ufo get reaction details failure
        exp.failure({
          metadata: {
            error: extractErrorInfo(error),
            reason: 'getDetailedReaction fetch failed'
          }
        });
      });
    });
    _defineProperty(this, "getState", function () {
      return _this.state;
    });
    _defineProperty(this, "onChange", function (callback) {
      _this.callbacks.push(callback);
    });
    _defineProperty(this, "removeOnChangeListener", function (toRemove) {
      _this.callbacks = _this.callbacks.filter(function (callback) {
        return callback !== toRemove;
      });
    });
    this.client = client;
    this.state = state !== null && state !== void 0 ? state : this.initialState;
    this.metadata = metadata;
  }
  _createClass(MemoryReactionsStore, [{
    key: "getReactionsState",
    value: function getReactionsState(containerAri, ari) {
      return this.state.reactions["".concat(containerAri, "|").concat(ari)];
    }
  }, {
    key: "setFlash",
    value: function setFlash(containerAri, ari, emojiId, flash) {
      this.setState({
        flash: _objectSpread(_objectSpread({}, this.state.flash), {}, _defineProperty({}, "".concat(containerAri, "|").concat(ari), _objectSpread(_objectSpread({}, this.state.flash["".concat(containerAri, "|").concat(ari)]), {}, _defineProperty({}, emojiId, flash))))
      });
    }
  }, {
    key: "setParticleEffectForEmoji",
    value: function setParticleEffectForEmoji(containerAri, ari, emojiId, enable) {
      this.setState({
        particleEffectByEmoji: _objectSpread(_objectSpread({}, this.state.particleEffectByEmoji), {}, _defineProperty({}, "".concat(containerAri, "|").concat(ari), _objectSpread(_objectSpread({}, this.state.particleEffectByEmoji["".concat(containerAri, "|").concat(ari)]), {}, _defineProperty({}, emojiId, enable))))
      });
    }
  }, {
    key: "withReadyReaction",
    value:
    /**
     * Utility function to help execute a callback to Reaction if its state is ready.
     *
     *
     * @param containerAri
     * @param ari
     *
     * @returns (updater: Updater<ReactionsReadyState>) => ReactionsState?
     *  A function that will execute the received callback with the ReactionsState if
     *  ready. If some state is returned, the new state will be applied.
     */
    function withReadyReaction(containerAri, ari) {
      var _this2 = this;
      return function (updater) {
        var reactionsState = _this2.getReactionsState(containerAri, ari);
        if (reactionsState && reactionsState.status === ReactionStatus.ready) {
          var updated = updater(reactionsState);
          if (updated) {
            _this2.setReactions(containerAri, ari, updated);
          }
        }
      };
    }

    /**
     * Utility function to help execute actions with a reaction. It handles reaction discovery
     * and branching between reacted and not reacted.
     *
     * @param reactedCallback callback that will be executed when the user has already reacted
     * with the emoji
     * @param notReactedCallback callback that will be executed when the user hasn't reacted
     * with the emoji
     *
     * @returns (containerAri: string, ari: string, emojiId: string) => ReactionsState?
     *  A function that will execute the correct callback to the triple containerAri, ari and
     *  emojiId. If some state is returned, the new state will be applied.
     */
  }, {
    key: "withReaction",
    value: function withReaction(reactedCallback, notReactedCallback) {
      var _this3 = this;
      return function (containerAri, ari, emojiId, onSuccess) {
        _this3.withReadyReaction(containerAri, ari)(function (reactionsState) {
          var reaction = reactionsState.reactions.find(byEmojiId(emojiId)) || {
            containerAri: containerAri,
            ari: ari,
            emojiId: emojiId,
            count: 0,
            reacted: false
          };
          var callback = reaction.reacted || !notReactedCallback ? reactedCallback : notReactedCallback;
          var updatedReaction = callback(reaction, onSuccess);
          if (updatedReaction && !(updatedReaction instanceof Function)) {
            return readyState(reactionsState.reactions.map(updateByEmojiId(emojiId, updatedReaction)));
          }
          return;
        });
      };
    }
  }]);
  return MemoryReactionsStore;
}();